import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { IImageLoadHandlerParams } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { ImageCropperPopup } from 'APP/main/PopupManager/Popups/ImageCropperPopup/ImageCropperPopup';
import { GroupType } from 'APP/model/group/groupModel.types';
import { Features } from 'APP/packages/features/Features';
import { useChannelValidations } from 'APP/packages/validations/forms/forms';

import { IChannelFormData } from './ChannelForm.types';

enum CreateChannelStep {
  GeneralInfo = 'general-info',
  Privacy = 'privacy',
}

interface IRadioOption {
  value: GroupType.ChannelOpen | GroupType.ChannelClosed;
  label: string;
  description: string;
}

interface IChannelFormPresenter {
  step: CreateChannelStep;
  categories: { label: string; value: string }[];
  initialValues: Partial<IChannelFormData>;
  isEditMode: boolean;
  nameValidate: any;
  categoryValidate: any;
  radioOptions: IRadioOption[];
  aliasValidate: any | null;
  isGeneralInfo: boolean;
  isPrivacy: boolean;
  isShowGeneralInfo: boolean;
  isShowPrivacy: boolean;
  isShowPrevStep: boolean;
  isShowSubmit: boolean;
  isShowCategory: boolean;
  isMonetization: boolean;
  setIsEditMode(mode: boolean): void;
  onShowGeneralInfo(): void;
  onShowPrivacy(): void;
  onImageLoad(params: IImageLoadHandlerParams): void;
}

export function useChannelFormPresenter(
  initialValues: Partial<IChannelFormData>,
  isEditMode: boolean
): IChannelFormPresenter {
  const { t } = useTranslation();
  const formValidations = useChannelValidations();

  delete initialValues.deepLinkAlias;

  const presenter = useLocalObservable<IChannelFormPresenter>(() => ({
    step: CreateChannelStep.GeneralInfo,
    categories: [],
    initialValues,
    isEditMode: isEditMode,

    nameValidate: formValidations.channelName.validate,
    categoryValidate: formValidations.channelCategory.validate,

    radioOptions: [
      {
        value: GroupType.ChannelOpen,
        label: t('channel_public_option'),
        description: t('channel_public_option_subhint'),
      },
      {
        value: GroupType.ChannelClosed,
        label: t('channel_private_option'),
        description: t('channel_private_option_subhint'),
      },
    ],

    get aliasValidate(): any | null {
      if (presenter.isEditMode) {
        return formValidations.channelAliasEdit.validate;
      }

      if (presenter.isPrivacy) {
        return formValidations.channelAliasCreate.validate;
      }

      return null;
    },

    get isGeneralInfo(): boolean {
      return presenter.step === CreateChannelStep.GeneralInfo;
    },

    get isPrivacy(): boolean {
      return presenter.step === CreateChannelStep.Privacy;
    },

    get isShowGeneralInfo(): boolean {
      return presenter.isEditMode || presenter.isGeneralInfo;
    },

    get isShowPrivacy(): boolean {
      return presenter.isEditMode || presenter.isPrivacy;
    },

    get isShowPrevStep(): boolean {
      return !presenter.isEditMode && presenter.isPrivacy;
    },

    get isShowSubmit(): boolean {
      return presenter.isEditMode || presenter.isPrivacy;
    },

    get isShowCategory(): boolean {
      return Features.isChannelCategoryChoiceEnabled();
    },

    get isMonetization(): boolean {
      return Features.isMonetizationEnabled();
    },

    setIsEditMode(mode: boolean): void {
      presenter.isEditMode = mode;
    },

    onShowGeneralInfo(): void {
      presenter.step = CreateChannelStep.GeneralInfo;
    },

    onShowPrivacy(): void {
      presenter.step = CreateChannelStep.Privacy;
    },

    onImageLoad(params: IImageLoadHandlerParams): void {
      Tasks.app.openPopup(ImageCropperPopup, { params });
    },
  }));

  useEffect(() => {
    presenter.initialValues = initialValues;
  }, [initialValues]);

  useEffect(() => {
    presenter.setIsEditMode(isEditMode);
  }, [isEditMode]);

  return presenter;
}
